import React, { useState, useCallback, useEffect } from "react";
import styled, { css } from "styled-components";
import { useLessonDetails } from "../hooks/useLessonDetails";
import LessonData from "./LessonData";
import Button from "./Button";
import Modal from "./Modal";
import Advice from "./Advice";
import StampMessage from "./StampMessage";
import { evaluate, sort, resolve } from "../helpers/lessons";
import CacheManager from "../managers/CacheManager";
import equal from "fast-deep-equal";
import { useAttempt } from "../hooks/useAttempt";
import UserAPI from "../apis/UserAPI";
import VideoHelp from "./VideoHelp";

const Lesson = ({
  id = undefined,
  onCompleted = () => new Promise((resolve, reject) => resolve()),
  max = 3,
  nextItem,
  nextItemHandler,
  hasNextLesson,
  onGoToNextLesson = () => { },
  onGoToChaptersView = () => { },
  isCompleted = false,
  readonly = false
}) => {
  const lesson = useLessonDetails(id);

  const lastAttempt = useAttempt(id);

  const [seed, setSeed] = useState({ seed: undefined });
  const [state, setState] = useState(undefined);

  const [showState, setShowState] = useState(undefined);
  const [showAdvice, setShowAdvice] = useState(true);
  const [showSolution, setShowSolution] = useState(false);
  const [showVideoHelp, setShowVideoHelp] = useState(false);

  const [processing, setProcessing] = useState(false);
  const onChange = useCallback((state) => setState(state), []);

  const onSubmit = () => {
    if (processing) return;
    setProcessing(true);

    const result = evaluate(lesson.data, state.data);

    setShowState(result.state);

    if (result) {
      UserAPI.sendAttempt(id, {
        state: result.state,
        seed: lesson.data,
        data: result.data,
      }).finally(() => {
        setSeed({ summary: true, seed: result.data });
        setProcessing(false);
      });
    } else setProcessing(false);
  };

  const onNewAttempt = () => {
    setSeed({ seed: sort(lesson.data) });
  };

  useEffect(() => {
    if (!lesson?.data) return;
    const cache = CacheManager.lessonOpen(id);
    const isValidCache = cache && equal(cache.seed, lesson.data);
    if (isValidCache) setSeed({ summary: cache.summary, seed: cache.memoized });
    else setSeed({ seed: sort(lesson.data) });
  }, [id, lesson?.data]);

  useEffect(() => {
    if (!state?.data || !lesson?.data) return;

    CacheManager.lessonSave(id, {
      summary: seed.summary,
      memoized: state?.data,
      seed: lesson?.data,
    });
  }, [id, seed, lesson, state]);

  useEffect(() => {
    const subscribers = {
      LESSON_AUDIO_LIST: true,
      LESSON_VIDEO: true,
      LESSON_BOX: true,
    };

    if (
      subscribers[getType(state?.data)] ||
      lastAttempt?.state === "SUCCESS" ||
      lastAttempt?.attempt >= max
    )
      onCompleted(id);
  }, [id, max, state, lastAttempt, onCompleted]);

  const ButtonSend = () => {
    const subscribers = {
      LESSON_DISCOVER_SENTENCE: true,
      LESSON_DISCOVER_WORD: true,
      LESSON_LIST_ORDER: true,
      LESSON_SELECT: true,
      LESSON_UNION: true,
      LESSON_UNION_WITH_BOX: true,
    };

    if (!subscribers[getType(seed.seed)]) return null;
    return <ButtonsWrapper><ButtonSticky text="Enviar" onClick={onSubmit} /></ButtonsWrapper>;
  };

  const finished =
    lastAttempt?.state === "SUCCESS" || lastAttempt?.attempt === 3;

  // console.log(`finished: ${finished} isCompleted: ${isCompleted}`);

  return (
    <Container>
      {showState && (
        <ModalStyled>
          <ContainerState>
            {showState === "SUCCESS" && <StampMessage stars text="Sehr gut!" />}
            {showState === "FAILURE" && (
              <StampMessage wrong text="Pech gehabt!" />
            )}
            <ButtonState
              text="OK"
              tertiary
              onClick={() => setShowState(false)}
              loading={processing}
            />
            {showState === "FAILURE" && (
              <ButtonHelp
                secondary
                text="¿Necesitas ayuda?"
                onClick={() => setShowVideoHelp(true)}
              />
            )}
          </ContainerState>
        </ModalStyled>
      )}
      {showVideoHelp && <VideoHelp onClose={() => setShowVideoHelp(false)} />}
      {showAdvice && lesson?.advice && (
        <Advice advice={lesson.advice} onAccept={() => setShowAdvice(false)} />
      )}
      {lesson?.description && <Description>{lesson?.description}</Description>}

      {lesson && !finished && (
        <>
          <LessonData
            seed={seed.seed}
            freeze={seed.summary}
            onChange={onChange}
          />
          {seed.summary ? (
            <ButtonsWrapper>
              <ButtonSticky
                text={
                  lastAttempt?.attempt === max
                    ? "Último intento"
                    : "Nuevo intento"
                }
                onClick={onNewAttempt}
              />
            </ButtonsWrapper>
          ) : (
            state?.ready && (
              <ButtonSend />
            )
          )}
        </>
      )}

      {lesson && finished && (
        <>
          <LessonData
            freeze
            seed={
              showSolution
                ? resolve(lastAttempt.seed, lastAttempt.data)?.data
                : lastAttempt.data
            }
          />
          {lastAttempt.state !== "SUCCESS" && (
            <ButtonsWrapper>
              {showSolution ? (
                <ButtonSticky
                  text="Ver mi respuesta"
                  onClick={() => setShowSolution(false)}
                  opaque
                />
              ) : (
                <ButtonSticky
                  text="Ver solución"
                  onClick={() => setShowSolution(true)}
                  tertiary
                />
              )}
              <ButtonStyled
                text={
                  hasNextLesson
                    ? "Ir al siguiente ejercicio"
                    : "Volver a los capítulos"
                }
                onClick={() => {
                  if (hasNextLesson) onGoToNextLesson();
                  else onGoToChaptersView();
                }}
                type="quartary"
              />
            </ButtonsWrapper>
          )}
        </>
      )}

      {!lesson && <Spinner src="loading-blue.gif" />}
      {lesson &&
        isCompleted &&
        (!finished || lastAttempt?.state === "SUCCESS") && (
          <ButtonsWrapper>
            <ButtonStyled
              text={
                hasNextLesson
                  ? "Ir al siguiente ejercicio"
                  : "Volver a los capítulos"
              }
              onClick={() => {
                if (hasNextLesson) onGoToNextLesson();
                else onGoToChaptersView();
              }}
              type="quartary"
            />
          </ButtonsWrapper>
        )}
    </Container>
  );
};

const getType = (data) => Object.keys(data || {})[0];

const Container = styled.div`
  width: 100%;
`;

const Description = styled.div`
  font-size: 0.9em;
  color: white;
  margin-bottom: 25px;
  white-space: pre-wrap;
`;

const ButtonSticky = styled(Button)`
  .button {
    background-color: rgba(33, 150, 243, 0.85);
    color: white;
  }

  ${(props) =>
    props.opaque &&
    css`
      .button {
        background-color: rgba(0, 0, 0, 0.08);
        color: white;
      }
    `}
`;

const ButtonStyled = styled(Button)``;

const ButtonsWrapper = styled.div`
  margin-top: 25px;
  position: sticky;
  display: flex;
  flex-direction: column;
  padding: 20px;
  bottom: 0px;

  background: rgb(50, 176, 234);
  background: linear-gradient(
    0deg,
    rgba(50, 176, 234, 1) 0%,
    rgba(50, 176, 234, 0.6166841736694677) 30%,
    rgba(255, 255, 255, 0) 80%
  );

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ContainerState = styled.div`
  padding: 20px;
  & > * {
    margin: 5px;
  }
`;

const ButtonState = styled(Button)`
  margin-top: 30px;
`;

const ModalStyled = styled(Modal)`
  max-width: 220px;
  width: 100%;
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

const ButtonHelp = styled(Button)`
  margin-top: 15px;
  .button {
    font-size: 0.8em;
  }
`;

export default Lesson;
